import { useEffect, useMemo, type FC, type PropsWithChildren } from 'react';
import { useColorScheme, ThemeProvider } from '@mui/material';
import { QueryClient } from '@tanstack/react-query';

import { Theme } from '#packages/web/theme';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';

import { useLoadAppSettingsQuery } from '#apps/client-web/features/settings/hooks/use-load-app-settings-query';
import { type AppSettingsService } from '#apps/client-web/features/settings/services/app-settings';
import { isNil } from '#packages/data-collection/libs/lodash';

export type AppThemeProviderProps = {
  /**
   * Application settings service.
   */
  appSettingsService: AppSettingsService;

  /**
   * Query client instance.
   */
  queryClient: QueryClient;
};

export const AppThemeProvider: FC<
  PropsWithChildren<AppThemeProviderProps & LoggingContext>
> = ({
  appSettingsService,
  queryClient,

  logger,

  children,
}) => {
  const { setMode } = useColorScheme();

  const loadAppSettingsQuery = useLoadAppSettingsQuery({
    appSettingsService,
    queryClient,
  });

  // Create theme based on user settings
  const theme = useMemo(() => {
    logger?.debug(
      { theme: loadAppSettingsQuery.data?.theme },
      'Creating theme from user settings',
    );

    return isNil(loadAppSettingsQuery.data?.theme.name)
      ? undefined
      : Theme.create(loadAppSettingsQuery.data.theme.name);
  }, [loadAppSettingsQuery.data]);

  // Update MUI color scheme mode when appearance setting changes
  useEffect(() => {
    logger?.debug(
      { appearance: loadAppSettingsQuery.data?.theme.appearance },
      'Updating color scheme mode',
    );

    !isNil(loadAppSettingsQuery.data?.theme.appearance) &&
      setMode(loadAppSettingsQuery.data?.theme.appearance);
  }, [loadAppSettingsQuery.data?.theme.appearance, setMode]);

  if (isNil(theme)) {
    logger?.debug('No theme available, not rendering ThemeProvider');
    return null;
  }

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
